const DEFAULT_MAX_BYTES = 300 * 1024;

export function compressionMaxBytes(level: 'low' | 'medium' | 'high' = 'medium'): number {
    if (level === 'low') return 600 * 1024;
    if (level === 'high') return 150 * 1024;
    return DEFAULT_MAX_BYTES;
}

interface WorkerResponse {
    id: number;
    buffer?: ArrayBuffer;
    contentType?: string;
    originalBytes?: number;
    compressedBytes?: number;
    width?: number;
    height?: number;
    error?: string;
}

export interface CompressedImage {
    blob: Blob;
    fileBase64: string;
    dataUrl: string;
    contentType: string;
    fileName: string;
    originalBytes: number;
    compressedBytes: number;
    width?: number;
    height?: number;
}

let worker: Worker | null = null;
let requestId = 0;
const pending = new Map<number, {
    resolve: (value: WorkerResponse) => void;
    reject: (error: Error) => void;
}>();

function getWorker(): Worker {
    if (!worker) {
        worker = new Worker(new URL('../workers/imageCompression.worker.ts', import.meta.url), { type: 'module' });
        worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
            const callback = pending.get(event.data.id);
            if (!callback) return;
            pending.delete(event.data.id);
            if (event.data.error) callback.reject(new Error(event.data.error));
            else callback.resolve(event.data);
        };
        worker.onerror = () => {
            for (const callback of pending.values()) callback.reject(new Error('浏览器图片压缩线程运行失败'));
            pending.clear();
            worker?.terminate();
            worker = null;
        };
    }
    return worker;
}

function sourceForBrowserFetch(source: string): string {
    if (source.startsWith('http://') || source.startsWith('https://')) {
        return `/api/proxy?target=${encodeURIComponent(source)}`;
    }
    return source;
}

async function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === 'string'
            ? resolve(reader.result)
            : reject(new Error('压缩图片编码失败'));
        reader.onerror = () => reject(new Error('压缩图片编码失败'));
        reader.readAsDataURL(blob);
    });
}

function extensionFor(contentType: string): string {
    if (contentType === 'image/jpeg') return 'jpg';
    if (contentType === 'image/webp') return 'webp';
    if (contentType === 'image/gif') return 'gif';
    return 'png';
}

export async function compressImageInBrowser(
    source: string | Blob,
    maxBytes: number = DEFAULT_MAX_BYTES,
): Promise<CompressedImage> {
    const sourceBlob = source instanceof Blob
        ? source
        : await fetch(sourceForBrowserFetch(source)).then(async response => {
            if (!response.ok) throw new Error(`下载待压缩图片失败 (${response.status})`);
            return response.blob();
        });

    const id = ++requestId;
    const sourceBuffer = await sourceBlob.arrayBuffer();
    const result = await new Promise<WorkerResponse>((resolve, reject) => {
        pending.set(id, { resolve, reject });
        getWorker().postMessage({
            id,
            buffer: sourceBuffer,
            contentType: sourceBlob.type || 'image/png',
            maxBytes,
        }, [sourceBuffer]);
    });

    if (!result.buffer || !result.contentType) throw new Error('浏览器未返回压缩图片');
    const blob = new Blob([result.buffer], { type: result.contentType });
    const dataUrl = await blobToDataUrl(blob);

    return {
        blob,
        fileBase64: dataUrl.replace(/^data:.*;base64,/, ''),
        dataUrl,
        contentType: result.contentType,
        fileName: `ai-image-${Date.now()}.${extensionFor(result.contentType)}`,
        originalBytes: result.originalBytes ?? sourceBlob.size,
        compressedBytes: result.compressedBytes ?? blob.size,
        width: result.width,
        height: result.height,
    };
}
